import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Box,
  Typography,
  Avatar,
  IconButton,
  Grid,
  Paper,
  Chip,
  Table,
  TableContainer,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Checkbox,
  CircularProgress,
  useTheme,
  alpha,
} from '@mui/material';
import {
  Close as CloseIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { Client, Invoice, User, CobrancaFinanceiro } from '../../../types';
import { useAuth } from '../../../contexts/AuthContext';
import { CobrancaFinanceiroService } from '../../../services/CobrancaFinanceiroService';
import userService from '../../../services/userService';
import { formatarValor, formatarData } from '../../../utils/formatToView';

/**
 * Calcula a diferença de dias entre a data de vencimento e hoje.
 */
const getDaysOverdue = (dueDate: string) => {
  const today = new Date();
  const due = new Date(dueDate);
  today.setHours(0, 0, 0, 0);
  due.setHours(0, 0, 0, 0);
  const diffTime = today.getTime() - due.getTime();
  const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
  return diffDays > 0 ? diffDays : 0;
};


interface CriarCobrancaModalProps {
  open: boolean;
  onClose: () => void;
  client: Client | null;
  overdueInvoices: Invoice[];
  onSuccess?: (cobranca: CobrancaFinanceiro) => void;
}


const CriarCobrancaModal: React.FC<CriarCobrancaModalProps> = ({
  open,
  onClose,
  client,
  overdueInvoices,
  onSuccess
}) => {
  const theme = useTheme();
  const { user } = useAuth();
  
  // Estados locais do modal
  const [selectedInvoicesIds, setSelectedInvoicesIds] = useState<number[]>([]);
  const [observacoes, setObservacoes] = useState('');
  const [prioridade, setPrioridade] = useState<'alta' | 'normal' | 'baixa'>('normal');
  const [responsavelId, setResponsavelId] = useState<number | ''>('');
  const [isSaving, setIsSaving] = useState(false);
  const [users, setUsers] = useState<User[]>([]);

  // Filtra as faturas desse cliente específico
  const clientInvoices = useMemo(() => {
    if (!client) return [];
    return overdueInvoices.filter(inv => inv.cliente?.id === client.id);
  }, [client, overdueInvoices]);

  // Calcula o valor total das faturas selecionadas
  const totalValueSelected = useMemo(() => {
    return clientInvoices
      .filter(inv => selectedInvoicesIds.includes(inv.id))
      .reduce((acc, inv) => {
        return acc + (Number(inv.valor) + (Number(inv.acrescimo) || 0) - (Number(inv.desconto) || 0));
      }, 0);
  }, [clientInvoices, selectedInvoicesIds]);


  // Carregar usuários para o seletor de responsável
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const response = await userService.getAllUsers({ limit: 100 });
        setUsers(response.users);
      } catch (error) {
        console.error('Erro ao carregar usuários:', error);
      }
    };
    if (open) fetchUsers();
  }, [open]);

  // Inicializa os campos quando o modal abre para um novo cliente
  useEffect(() => {
    if (open && client) {
      setSelectedInvoicesIds(clientInvoices.map(inv => inv.id));
      setObservacoes('');
      setPrioridade('normal');
      setResponsavelId((client as any).vendedor_id || user?.id || '');
    }
  }, [open, client, clientInvoices, user]);

  const handleToggleInvoice = (id: number) => {
    setSelectedInvoicesIds(prev =>
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  const handleCreateCobranca = async () => {
    if (!client || selectedInvoicesIds.length === 0) {
      toast.warning('Selecione pelo menos uma fatura');
      return;
    }

    if (!responsavelId) {
      toast.warning('Selecione um responsável');
      return;
    }

    try {
      setIsSaving(true);
      
      const createdCobranca = await CobrancaFinanceiroService.create({
        cliente_id: client.id,
        faturas_id: selectedInvoicesIds,
        escritorio_id: client.escritorio_id,
        status: 'pendente',
        prioridade: prioridade,
        valor_original: totalValueSelected,
        data_cobranca: new Date().toISOString(),
        observacoes: observacoes,
        responsavel_id: Number(responsavelId),
      });

      toast.success('Cobrança criada com sucesso!');
      if (onSuccess) onSuccess(createdCobranca);

      onClose();
    } catch (error) {
      console.error('Erro ao criar cobrança:', error);
      toast.error('Erro ao criar cobrança');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={() => !isSaving && onClose()}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 3 }
      }}
    >
      <DialogTitle sx={{ m: 0, p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', bgcolor: 'grey.50' }}>
        <Box display="flex" alignItems="center" gap={1.5}>
          <Avatar sx={{ bgcolor: 'primary.main' }}>
            <AddIcon />
          </Avatar>
          <Box>
            <Typography variant="h6" fontWeight="800">Criar Nova Cobrança</Typography>
            <Typography variant="caption" color="text.secondary">Cliente: {client?.nome}</Typography>
          </Box>
        </Box>
        <IconButton onClick={onClose} disabled={isSaving}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 3 }}>
        <Grid container spacing={3}>
          {/* Dados do Cliente */}
          <Grid item xs={12}>
            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, bgcolor: alpha(theme.palette.primary.main, 0.02) }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={4}>
                  <Typography variant="caption" color="text.secondary" display="block">CPF/CNPJ</Typography>
                  <Typography variant="body2" fontWeight="600">{client?.cpf_cnpj}</Typography>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Typography variant="caption" color="text.secondary" display="block">Telefone</Typography>
                  <Typography variant="body2" fontWeight="600">
                    {client?.contatos?.find(c => c.is_default)?.numero || 
                     client?.contatos?.find(c => c.tipo === 'celular' || c.tipo === 'fixo')?.numero || 
                     'N/A'}
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={4}>
                  <Typography variant="caption" color="text.secondary" display="block">Endereço</Typography>
                  <Typography variant="body2" fontWeight="600" noWrap>
                    {client?.endereco}, {client?.numero} - {client?.bairro}
                  </Typography>
                </Grid>
              </Grid>
            </Paper>
          </Grid>

          {/* Configuração da Cobrança */}
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth size="small">
              <InputLabel>Prioridade</InputLabel>
              <Select
                value={prioridade}
                label="Prioridade"
                onChange={(e) => setPrioridade(e.target.value as any)}
              >
                <MenuItem value="baixa">Baixa</MenuItem>
                <MenuItem value="normal">Normal</MenuItem>
                <MenuItem value="alta">Alta</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6}>
            <FormControl fullWidth size="small">
              <InputLabel>Responsável pela Cobrança</InputLabel>
              <Select
                value={responsavelId}
                label="Responsável pela Cobrança"
                onChange={(e) => setResponsavelId(e.target.value as number)}
              >
                {users.map((u) => (
                  <MenuItem key={u.id} value={u.id}>
                    {u.nome} ({u.perfil})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Observações / Histórico Inicial"
              multiline
              rows={3}
              variant="outlined"
              value={observacoes}
              onChange={(e) => setObservacoes(e.target.value)}
              placeholder="Ex: Cliente alega que não recebeu o boleto..."
            />
          </Grid>

          {/* Tabela de Faturas */}
          <Grid item xs={12}>
            <Typography variant="subtitle2" fontWeight="700" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
              Faturas Incluídas na Cobrança
              <Chip
                label={`${selectedInvoicesIds.length} selecionada(s)`}
                size="small"
                color="primary"
                sx={{ height: 20, fontSize: '0.7rem' }}
              />
            </Typography>
            <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2, maxHeight: 250 }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox" sx={{ bgcolor: 'grey.50' }}>
                      <Checkbox
                        size="small"
                        indeterminate={selectedInvoicesIds.length > 0 && selectedInvoicesIds.length < clientInvoices.length}
                        checked={clientInvoices.length > 0 && selectedInvoicesIds.length === clientInvoices.length}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedInvoicesIds(clientInvoices.map(i => i.id));
                          } else {
                            setSelectedInvoicesIds([]);
                          }
                        }}
                      />
                    </TableCell>
                    <TableCell sx={{ bgcolor: 'grey.50', fontWeight: '700' }}>ID</TableCell>
                    <TableCell sx={{ bgcolor: 'grey.50', fontWeight: '700' }}>Vencimento</TableCell>
                    <TableCell align="center" sx={{ bgcolor: 'grey.50', fontWeight: '700' }}>Atraso</TableCell>
                    <TableCell sx={{ bgcolor: 'grey.50', fontWeight: '700' }}>Descrição</TableCell>
                    <TableCell align="right" sx={{ bgcolor: 'grey.50', fontWeight: '700' }}>Valor</TableCell>

                  </TableRow>
                </TableHead>
                <TableBody>
                  {clientInvoices.map((inv) => (
                    <TableRow key={inv.id} hover onClick={() => handleToggleInvoice(inv.id)} sx={{ cursor: 'pointer' }}>
                      <TableCell padding="checkbox">
                        <Checkbox
                          size="small"
                          checked={selectedInvoicesIds.includes(inv.id)}
                          readOnly
                        />
                      </TableCell>
                      <TableCell>#{inv.id}</TableCell>
                      <TableCell>{formatarData(inv.data_vencimento)}</TableCell>
                      <TableCell align="center">
                        <Chip 
                          label={`${getDaysOverdue(inv.data_vencimento)} dias`} 
                          size="small" 
                          variant="outlined"
                          color={getDaysOverdue(inv.data_vencimento) > 30 ? 'error' : 'warning'}
                          sx={{ fontSize: '0.65rem', height: 20 }}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontSize: '0.75rem' }} noWrap>
                          {inv.descricao}
                        </Typography>
                      </TableCell>

                      <TableCell align="right" sx={{ fontWeight: '600' }}>
                        {formatarValor(Number(inv.valor) + (Number(inv.acrescimo) || 0) - (Number(inv.desconto) || 0))}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            
            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 2 }}>
              <Typography variant="subtitle2" color="text.secondary">Total Selecionado:</Typography>
              <Typography variant="h6" fontWeight="800" color="primary.main">
                {formatarValor(totalValueSelected)}
              </Typography>
            </Box>
          </Grid>

        </Grid>
      </DialogContent>

      <DialogActions sx={{ p: 2, px: 3, bgcolor: 'grey.50' }}>
        <Button onClick={onClose} color="inherit" disabled={isSaving}>
          Cancelar
        </Button>
        <Button
          onClick={handleCreateCobranca}
          variant="contained"
          color="primary"
          startIcon={isSaving ? <CircularProgress size={20} color="inherit" /> : <AddIcon />}
          disabled={isSaving || selectedInvoicesIds.length === 0}
          sx={{ borderRadius: 2, px: 3 }}
        >
          {isSaving ? 'Salvando...' : 'Criar Cobrança'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CriarCobrancaModal;
